import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useCatalog from '../lib/useCatalog';
import { useCatalogStatus } from '../lib/Catalog';
import { autoOrder, routeFor, searchReleases, startBest } from '../lib/playback';
import { useLibrary } from '../lib/Library';
import Art from './Art';
import PosterCard, { useOpenTitle } from './PosterCard';
import CatalogNotice from './CatalogNotice';
import Rail from './Rail';
import ServiceSwitcher from './ServiceSwitcher';
import { useBrowseService, useServiceHome, useServices } from '../lib/services';

const rowKey = (entry) => entry.key || entry.torrentId || entry.title;

// The sports guide is re-asked at this cadence while something is on, so the
// score on the hero keeps up with the match
const LIVE_REFRESH_MS = 30_000;

/**
 * Whatever is live right now with a stream behind it, for the hero.
 *
 * Asks the sport tabs which have anything on, then the guide for the first
 * that does; the first live fixture with a mirror wins. Nothing on means null,
 * and the hero falls back to the catalog's own pick.
 */
const useLiveNow = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const { data } = await axios.get('/api/live/sports');
        const sport = (data.data || []).find((entry) => entry.live > 0);
        if (!sport) {
          if (!cancelled) setLive(null);
          return;
        }
        const guide = await axios.get('/api/live/guide', { params: { sport: sport.id } });
        const item = (guide.data.data?.items || []).find((entry) => entry.state === 'in' && entry.streams?.length);
        if (!cancelled) setLive(item ? { ...item, sport: sport.id } : null);
      } catch (_) {
        if (!cancelled) setLive(null);
      }
    };

    check();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return live;
};

/**
 * A match that is on right now: score, clock, and a way straight into it.
 *
 * A strip under the hero rather than the hero itself. The film's backdrop is
 * the one big picture on the page and the owner wants it there; a match has
 * no picture of its own, only crests, and does better as a band with the
 * score on it than as a dark hero pretending to be artwork.
 */
const LiveNow = ({ item }) => {
  const navigate = useNavigate();
  const home = item.home;
  const away = item.away;
  const feeds = item.streams?.length || 0;
  const watch = () => navigate('/sports', { state: { sport: item.sport, watch: item.id } });

  return (
    <section className="live-now">
      <div className="live-now-crests">
        {[home, away].map((team, index) =>
          team?.logo ? <img key={index} src={team.logo} alt="" className="live-now-crest" /> : null
        )}
      </div>
      <div className="live-now-main">
        <div className="hero-line">
          <span className="hero-live">
            <span className="hero-live-dot" />
            LIVE
          </span>
          <span className="kicker-text">{(item.league?.name || item.sport || '').toUpperCase()}</span>
          {feeds ? <span className="hero-dim">{feeds} feed{feeds === 1 ? '' : 's'}</span> : null}
        </div>
        <h2 className="live-now-title">
          {home && away ? (
            <>
              {home.short || home.name} <span className="hero-vs">vs</span> {away.short || away.name}
            </>
          ) : (
            item.name
          )}
        </h2>
        <div className="hero-score-line">
          {home?.score != null && away?.score != null ? (
            <span className="hero-score">
              {home.score} – {away.score}
            </span>
          ) : null}
          <span className="hero-clock">
            <span className="dot dot--ok" />
            {item.status?.detail || item.status?.short || 'Live'}
          </span>
          {item.venue ? <span className="hero-dim">{item.venue}</span> : null}
        </div>
      </div>
      <div className="live-now-actions">
        <button type="button" className="btn btn--play" onClick={watch}>
          <span className="play-tri" />
          Watch live
        </button>
        <Link to="/sports" state={{ sport: item.sport }} className="btn btn--soft">
          <span className="info-i">i</span>
          Match centre
        </Link>
      </div>
    </section>
  );
};

/** The hero on an ordinary day: the catalog's pick, with its trailer behind. */
const TitleHero = ({ hero }) => {
  const navigate = useNavigate();
  const openTitle = useOpenTitle();

  return (
    <section className="hero">
      <div className="hero-bg">
        {hero.backdrop && <img src={hero.backdrop} alt="" />}
      </div>
      <div className="hero-wash" />
      <div className="hero-fade" />
      <div className="hero-body">
        <div className="kicker">
          #1 TRENDING THIS WEEK
          <span className="kicker-rule" />
          <span className="kicker-alt">{hero.type === 'series' ? 'SERIES' : 'FILM'}</span>
        </div>
        <h1 className="hero-title">{hero.name}</h1>
        <div className="meta-line">
          {hero.rating ? <span className="meta-star">★ {hero.rating}</span> : null}
          {hero.year ? <span>{hero.year}</span> : null}
          {hero.certification ? <span className="cert">{hero.certification}</span> : null}
          {hero.runtime ? <span>{hero.runtime}</span> : null}
          {hero.genres?.length ? <span>{hero.genres.slice(0, 2).join(' · ')}</span> : null}
          <span className="cert cert--hd">HD</span>
        </div>
        {hero.overview && <p className="hero-blurb">{hero.overview}</p>}
        <div className="hero-actions">
          <button
            type="button"
            className="btn btn--play"
            onClick={() => navigate(`/title/${hero.type}/${hero.id}`, { state: { autoplay: true } })}
          >
            <span className="play-tri" />
            Play
          </button>
          <button type="button" className="btn btn--soft" onClick={() => openTitle(hero)}>
            <span className="info-i">i</span>
            More info
          </button>
        </div>
      </div>
    </section>
  );
};

/**
 * The rail of half-watched things.
 *
 * Resuming is not a link. Whatever played this last time is gone by now — a
 * torrent this server stopped seeding, or a debrid link that expired the same
 * evening — so the row does what the play button did in the first place: run
 * the search again, open the best release it finds, and hand the player the
 * timestamp that was kept alongside it.
 */
const ContinueWatching = ({ username }) => {
  const [entries, setEntries] = useState([]);
  const [starting, setStarting] = useState(null);
  const [failed, setFailed] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get('/api/library/history')
      // Finished titles have nothing left to continue
      .then(({ data }) => setEntries((data.data || []).filter((item) => (item.progress || 0) < 0.95)))
      .catch(() => setEntries([]));
  }, []);

  const resume = async (entry) => {
    const find = entry.find || {};

    // Nothing to search on: a stream opened by URL, or a row written before any
    // of this was kept. The old route is all there is, stale as it may be.
    if (!find.title) {
      navigate(entry.resumeTo || `/player/${entry.torrentId}/0`);
      return;
    }

    setStarting(rowKey(entry));
    setFailed('');

    try {
      const { results, meta } = await searchReleases({
        name: find.title,
        originalName: find.originalName,
        originalLanguage: find.originalLanguage,
        // Resumed in the language it was being watched in - someone who
        // switched a film to French mid-way comes back to French. Rows from
        // before languages existed carry none and get the profile default.
        audioLanguage: find.audioLanguage || undefined,
        type: find.type,
        imdbId: find.imdbId,
        year: find.year,
        season: find.season ?? undefined,
        episode: find.episode ?? undefined
      });

      if (!results.length) {
        setFailed(`Nothing available for ${find.title} on the indexers we can reach.`);
        return;
      }

      const { release, target } = await startBest(
        // The rail remembers what to search for but not how long it runs, so
        // this leans on the assumed runtime for the type
        autoOrder(results, { type: find.type }),
        {
          debrid: meta?.debrid,
          season: find.season ?? undefined,
          episode: find.episode ?? undefined
        }
      );

      navigate(routeFor(target), {
        state: {
          ...find,
          subtitle: entry.subtitle,
          torrentId: release.infoHash,
          infoHash: release.infoHash,
          resolution: release.resolution,
          // Seconds rather than the progress bar's fraction: this is a
          // different release with its own runtime, and the fraction would
          // land somewhere else in the film.
          resumeAt: entry.position || 0
        }
      });
    } catch (err) {
      setFailed(err.response?.data?.error || `Could not reopen ${find.title}.`);
    } finally {
      setStarting(null);
    }
  };

  if (!entries.length) return null;

  return (
    <section className="row">
      <div className="row-head">
        <h2 className="row-title">Continue watching{username ? ` for ${username}` : ''}</h2>
        <span className="row-note">{entries.length} in progress</span>
      </div>
      {failed && <div className="alert row-alert">{failed}</div>}
      <Rail className="rail rail--wide">
        {entries.slice(0, 12).map((entry) => (
          <button
            key={rowKey(entry)}
            type="button"
            className="still-card"
            onClick={() => resume(entry)}
            disabled={Boolean(starting)}
          >
            <Art src={entry.backdrop} alt={entry.title} label="STILL 16:9" className="art--still">
              <span className="still-play">
                <span className="play-tri play-tri--white" />
              </span>
              <span className="still-caption">
                <h4>{entry.title}</h4>
                <p>
                  {starting === rowKey(entry)
                    ? 'finding a release…'
                    : [entry.subtitle, entry.remaining].filter(Boolean).join(' · ') || 'resume'}
                </p>
              </span>
              <span className="progress">
                <span style={{ width: `${Math.round((entry.progress || 0) * 100)}%` }} />
              </span>
            </Art>
          </button>
        ))}
      </Rail>
    </section>
  );
};

/** One shelf of title cards, shared by the catalog's rows and the personalized ones. */
const PosterRow = ({ row }) => (
  <section className="row">
    <div className="row-head">
      {row.logo && <img className="row-logo" src={row.logo} alt="" />}
      <h2 className="row-title">{row.title}</h2>
      <span className="row-note">{row.note}</span>
      {row.query && (
        <Link to={`/discover?${row.query}`} className="row-more">
          explore all →
        </Link>
      )}
    </div>
    <Rail>
      {row.items.map((title) => (
        <PosterCard key={`${title.type}-${title.id}`} title={title} />
      ))}
    </Rail>
  </section>
);

/**
 * The rows built from what this account actually watches — "Because you
 * watched", "Top picks". Fetched apart from /home so a slow personalization
 * pass (it may be resolving history rows against the catalog) never holds up
 * first paint, and rendered only once there is something to say.
 */
const ForYou = () => {
  const { can } = useCatalogStatus();
  const { data } = useCatalog(can('recommendations') ? '/api/catalog/for-you' : null);
  const rows = data?.rows || [];

  return rows.map((row) => <PosterRow key={row.key} row={row} />);
};

/** The saved list, surfaced on home instead of only behind its nav entry. */
const MyListRow = () => {
  const { items } = useLibrary();
  if (!items.length) return null;

  return (
    <section className="row">
      <div className="row-head">
        <h2 className="row-title">My list</h2>
        <span className="row-note">{items.length} saved</span>
        <Link to="/list" className="row-more">
          see all →
        </Link>
      </div>
      <Rail>
        {items.slice(0, 12).map((title) => (
          <PosterCard key={`${title.type}-${title.id}`} title={title} shape="poster" />
        ))}
      </Rail>
    </section>
  );
};

const TopTen = ({ items }) => {
  const openTitle = useOpenTitle();
  if (!items.length) return null;

  return (
    <section className="row">
      <div className="row-head">
        <h2 className="row-title">Top 10 today</h2>
        <span className="row-note">by popularity · worldwide</span>
      </div>
      <Rail className="rail rail--ten">
        {items.map((title, index) => (
          <a
            key={`${title.type}-${title.id}`}
            href={`/title/${title.type}/${title.id}`}
            className="top-ten"
            onClick={(event) => {
              event.preventDefault();
              openTitle(title);
            }}
          >
            <span className="top-ten-num">{index + 1}</span>
            <span className="top-ten-art">
              <Art src={title.poster} alt={title.name} label="POSTER" className="art--poster">
                {!title.poster && <span className="art-title">{title.name}</span>}
              </Art>
            </span>
          </a>
        ))}
      </Rail>
    </section>
  );
};

/** The shape of the page before the catalog answers, so nothing jumps. */
const HomeSkeleton = () => (
  <div className="home">
    <div className="hero">
      <div className="skeleton hero-skeleton" />
    </div>
    <div className="home-rows">
      {[0, 1].map((index) => (
        <section className="row" key={index}>
          <div className="row-head">
            <div className="skeleton skeleton-title" />
          </div>
          <div className="rail">
            {Array.from({ length: 6 }, (_, cell) => (
              <div key={cell} className="skeleton skeleton-card" />
            ))}
          </div>
        </section>
      ))}
    </div>
  </div>
);

const Home = ({ username }) => {
  const { data, loading, error, notConfigured } = useCatalog('/api/catalog/home');
  const live = useLiveNow();
  // A chosen streaming service turns the whole page into that service's:
  // its own hero and shelves, with what is personal (continue watching, the
  // saved list) kept as it is
  const [serviceId, setServiceId] = useBrowseService();
  const { find } = useServices();
  const scoped = useServiceHome(serviceId, serviceId ? find(serviceId) : null);

  if (loading) return <HomeSkeleton />;
  if (notConfigured) {
    return (
      <div className="page">
        <CatalogNotice />
        <div className="home-rows">
          <ContinueWatching username={username} />
        </div>
      </div>
    );
  }
  if (error) return <div className="page alert">{error}</div>;

  const { hero, topTen = [], rows = [], collections = [] } = data;

  if (scoped) {
    return (
      <div className="home">
        {scoped.hero ? (
          <TitleHero hero={scoped.hero} />
        ) : (
          <div className="hero">
            <div className="skeleton hero-skeleton" />
          </div>
        )}

        <div className="home-rows">
          <ServiceSwitcher value={serviceId} onChange={setServiceId} />
          <ContinueWatching username={username} />
          <MyListRow />
          {scoped.rows.map((row) => (
            <PosterRow key={row.key} row={row} />
          ))}
          {!scoped.loading && !scoped.rows.length && (
            <div className="browse-empty">
              <p>Nothing to show for this service right now.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {hero && <TitleHero hero={hero} />}

      <div className="home-rows">
        <ServiceSwitcher value={serviceId} onChange={setServiceId} />
        {live && <LiveNow item={live} />}
        <ContinueWatching username={username} />
        <TopTen items={topTen} />
        <ForYou />
        <MyListRow />

        {rows.map((row) => (
          <PosterRow key={row.key} row={row} />
        ))}

        {collections.length > 0 && (
          <section className="coll-section">
            <div className="row-head row-head--flush">
              <h2 className="row-title">Collections</h2>
              <span className="row-note">saved sorts, recomputed on open</span>
            </div>
            <div className="coll-grid">
              {collections.map((collection) => (
                <Link key={collection.key} to={`/discover?${collection.query}`} className="coll">
                  {collection.backdrop && <img src={collection.backdrop} alt="" />}
                  <span className="coll-body">
                    <span className="coll-kicker">{collection.kicker}</span>
                    <span className="coll-title">{collection.title}</span>
                    <span className="coll-note">{collection.note}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Home;
